import React, { useState } from 'react';
import {
  // eslint-disable-next-line no-restricted-imports
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  Text,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { validateEmail } from 'src/utils/email';
import { CustomerRole } from 'src/contexts/AuthContext';

type InviteUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    email: string;
    role: CustomerRole;
    displayName?: string;
  }) => void;
  isLoading: boolean;
};

export const InviteUserModal: React.FC<InviteUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<CustomerRole>(CustomerRole.USER);
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    setError(null);

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(email.trim())) {
      setError('Invalid email address');
      return;
    }

    onSubmit({
      email: email.trim(),
      role,
      displayName: displayName.trim() || undefined,
    });
  };

  const handleClose = () => {
    setEmail('');
    setRole(CustomerRole.USER);
    setDisplayName('');
    setError(null);
    onClose();
  };

  const isValid = email.trim() && validateEmail(email.trim());

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent borderRadius="16px">
        <ModalCloseButton />
        <ModalHeader pb={2} pt={6}>
          <Text
            fontFamily="marketingPage"
            fontWeight="bold"
            fontSize="xl"
            color="text.title">
            Invite New User
          </Text>
        </ModalHeader>

        <ModalBody px={6} py={4}>
          <VStack align="stretch" spacing={4}>
            <FormControl isRequired>
              <FormLabel
                fontFamily="marketingPage"
                fontWeight="semibold"
                fontSize="sm"
                color="text.title"
                mb={2}>
                Email
              </FormLabel>
              <Input
                type="email"
                placeholder="colleague@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                border="1px solid"
                borderColor="border.box"
                borderRadius="md"
                fontFamily="marketingPage"
                _focus={{
                  borderColor: 'icon.green',
                  boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                }}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel
                fontFamily="marketingPage"
                fontWeight="semibold"
                fontSize="sm"
                color="text.title"
                mb={2}>
                Role
              </FormLabel>
              <Select
                value={role}
                onChange={(e) => setRole(e.target.value as CustomerRole)}
                border="1px solid"
                borderColor="border.box"
                borderRadius="md"
                fontFamily="marketingPage"
                _focus={{
                  borderColor: 'icon.green',
                  boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                }}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel
                fontFamily="marketingPage"
                fontWeight="semibold"
                fontSize="sm"
                color="text.title"
                mb={2}>
                Display Name (Optional)
              </FormLabel>
              <Input
                placeholder="John Doe"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                border="1px solid"
                borderColor="border.box"
                borderRadius="md"
                fontFamily="marketingPage"
                _focus={{
                  borderColor: 'icon.green',
                  boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                }}
              />
            </FormControl>

            {error && (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter gap={3} px={6} pb={6}>
          <Button
            bg="surface.control"
            color="text.title"
            size="md"
            px={6}
            py={2}
            borderRadius="md"
            fontWeight="600"
            buttonName="cancel-invite"
            fontFamily="marketingPage"
            onClick={handleClose}
            flex={1}
            _hover={{ bg: 'surface.controlHover' }}>
            Cancel
          </Button>
          <Button
            bg="icon.green"
            color="white"
            size="md"
            px={6}
            py={2}
            borderRadius="md"
            fontWeight="600"
            buttonName="send-invite"
            fontFamily="marketingPage"
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={!isValid}
            flex={1}
            _hover={{ bg: 'green.600' }}>
            Send Invite
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
